import type { TypedDocumentString } from './generated/graphql'

/**
 * The GraphQL transport: `fetch`, a JSON body, and nothing else.
 *
 * There is no Apollo, urql or normalizing cache here, and that is a decision
 * rather than an omission.
 *
 * The contract this client talks to is built around a normalizing cache being
 * the wrong tool. Scanning is a mutation precisely because a cached scan result
 * is "a stale answer presented as a current one, which for a security tool is a
 * false pass wearing a different hat". The schema goes further and leaves
 * `Finding` without an `id` field on purpose, because a normalizing cache keyed
 * on `id` plus `__typename` would merge the dozen findings that share a check id
 * into one and silently drop eleven real results.
 *
 * A client with no cache cannot make either mistake. It also removes the retry
 * behaviour that a scan must not have: nothing here retries a mutation.
 *
 * The report page's own polling loop is the only thing that repeats a request,
 * it only ever repeats a query, and it owns its own cancellation.
 */

export type TransportErrorKind = 'network' | 'http' | 'graphql' | 'malformed'

export class TransportError extends Error {
  readonly kind: TransportErrorKind
  readonly status?: number

  constructor(kind: TransportErrorKind, message: string, status?: number) {
    super(message)
    this.name = 'TransportError'
    this.kind = kind
    this.status = status
  }

  /**
   * What to put in front of a person.
   *
   * Never the server's own error text. A top-level GraphQL error means the
   * server broke, and the contract is explicit that such an error carries no Go
   * error string, hostname or internal address. Repeating whatever did arrive
   * would undo that at the last step, so the wording here is ours and the
   * detail stays in the console.
   */
  get userMessage(): string {
    switch (this.kind) {
      case 'network':
        return 'Could not reach the scanner. Check your connection and try again.'
      case 'http':
        return this.status === 429
          ? 'Too many requests. Wait a moment and try again.'
          : 'The scanner is not answering right now. Try again in a moment.'
      case 'graphql':
      case 'malformed':
        return 'The scanner returned something this page could not read. This is our fault, not yours.'
    }
  }
}

interface GraphQLResponse<T> {
  data?: T | null
  errors?: { message: string }[]
}

export const GRAPHQL_ENDPOINT = '/graphql'

export interface ExecuteOptions {
  signal?: AbortSignal
}

export async function execute<TResult, TVariables>(
  document: TypedDocumentString<TResult, TVariables>,
  ...args: TVariables extends Record<string, never>
    ? [variables?: undefined, options?: ExecuteOptions]
    : [variables: TVariables, options?: ExecuteOptions]
): Promise<TResult> {
  const [variables, options] = args

  let response: Response
  try {
    response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        accept: 'application/graphql-response+json, application/json',
      },
      body: JSON.stringify({
        query: document.toString(),
        operationName: operationNameOf(document),
        variables: variables ?? {},
      }),
      // Nothing here is authenticated and the scan id in the URL is the whole
      // authorization story, so no cookies are wanted or sent.
      credentials: 'omit',
      signal: options?.signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new TransportError('network', 'fetch failed')
  }

  if (!response.ok) {
    throw new TransportError('http', `HTTP ${response.status}`, response.status)
  }

  let payload: GraphQLResponse<TResult>
  try {
    payload = (await response.json()) as GraphQLResponse<TResult>
  } catch {
    throw new TransportError('malformed', 'response was not JSON')
  }

  if (payload.errors && payload.errors.length > 0) {
    // Logged, not shown. See `userMessage`.
    console.error('GraphQL error', payload.errors)
    throw new TransportError('graphql', payload.errors[0]?.message ?? 'server error')
  }

  if (payload.data === null || payload.data === undefined) {
    throw new TransportError('malformed', 'response carried no data')
  }

  return payload.data
}

/**
 * Pulled off the document text so the request carries `operationName`.
 *
 * Servers use it for logging, for persisted-operation lookup and for rate
 * limiting per operation, and sending it costs nothing.
 */
function operationNameOf(document: { toString(): string }): string | undefined {
  const match = /^\s*(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/.exec(
    document.toString(),
  )
  return match?.[1]
}
